import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Lightbulb, TrendingDown, Shield, Users, CheckCircle, AlertTriangle, Loader2,
  RefreshCw, ChevronRight, ChevronDown, ClipboardList, Target, BookOpen, Compass,
  FileText, Building2, Scale, Clock, TrendingUp, BarChart3, PoundSterling,
  Download, Save, CheckSquare, Square, Lock,
} from 'lucide-react';
import api from '@/lib/api';
import { cleanMarkdown } from '@/lib/formatAIText';
import { useUser } from '@/lib/UserContext';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  cost_savings: TrendingDown,
  risk_mitigation: Shield,
  consolidation: Users,
  compliance: CheckCircle,
  strategic: Lightbulb,
};

const CATEGORY_COLORS = {
  cost_savings: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800',
  risk_mitigation: 'text-amber-600 bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800',
  consolidation: 'text-blue-600 bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800',
  compliance: 'text-violet-600 bg-violet-50 dark:bg-violet-950/40 border-violet-200 dark:border-violet-800',
  strategic: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-950/40 border-indigo-200 dark:border-indigo-800',
};

const PRIORITY_STYLES = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-emerald-500',
};

const SECTION_META = {
  'quick-start checklist': { icon: CheckSquare, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800' },
  'estimated roi': { icon: PoundSterling, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800' },
  'objective': { icon: Target, color: 'text-blue-600', bg: '' },
  'preparation & homework': { icon: BookOpen, color: 'text-purple-600', bg: '' },
  // Sourcing / Cost Savings sections
  'sourcing strategy': { icon: Compass, color: 'text-cyan-600', bg: '' },
  'tender / rfx requirements': { icon: FileText, color: 'text-orange-600', bg: '' },
  'potential suppliers / supply market': { icon: Building2, color: 'text-indigo-600', bg: '' },
  'negotiation strategy': { icon: Scale, color: 'text-rose-600', bg: '' },
  // Compliance sections
  'policy & governance': { icon: Shield, color: 'text-violet-600', bg: '' },
  'system & process changes': { icon: Compass, color: 'text-cyan-600', bg: '' },
  'communication & training': { icon: Users, color: 'text-orange-600', bg: '' },
  'monitoring & escalation': { icon: BarChart3, color: 'text-red-600', bg: '' },
  // Risk Mitigation sections
  'risk assessment': { icon: Shield, color: 'text-red-600', bg: '' },
  'mitigation actions': { icon: Target, color: 'text-amber-600', bg: '' },
  'contingency planning': { icon: BookOpen, color: 'text-indigo-600', bg: '' },
  'governance & monitoring': { icon: BarChart3, color: 'text-violet-600', bg: '' },
  // Strategic / Process sections
  'process redesign': { icon: Compass, color: 'text-cyan-600', bg: '' },
  'technology & system requirements': { icon: FileText, color: 'text-indigo-600', bg: '' },
  'change management': { icon: Users, color: 'text-orange-600', bg: '' },
  'adoption & sustainability': { icon: TrendingUp, color: 'text-emerald-600', bg: '' },
  // Common sections
  'raci': { icon: Users, color: 'text-teal-600', bg: '' },
  'implementation timeline': { icon: Clock, color: 'text-violet-600', bg: '' },
  'key risks & mitigations': { icon: Shield, color: 'text-red-600', bg: '' },
  'success kpis': { icon: BarChart3, color: 'text-sky-600', bg: '' },
  'expected outcomes': { icon: TrendingUp, color: 'text-emerald-600', bg: '' },
};

function parseSections(text) {
  if (!text) return [];
  const sections = [];
  let current = null;
  for (const line of text.split('\n')) {
    // Match **Heading**: rest OR standalone **Heading** on its own line
    const matchColon = line.match(/^\*\*(.+?)\*\*:\s*(.*)/);
    const matchStandalone = !matchColon && line.match(/^\*\*(.+?)\*\*\s*$/);
    if (matchColon || matchStandalone) {
      if (current) sections.push(current);
      const heading = matchColon ? matchColon[1].trim() : matchStandalone[1].trim();
      const rest = matchColon ? matchColon[2].trim() : '';
      const key = heading.toLowerCase();
      const meta = SECTION_META[key] || { icon: Lightbulb, color: 'text-primary', bg: '' };
      current = { heading, key, lines: rest ? [rest] : [], ...meta };
    } else if (current && line.trim()) {
      current.lines.push(line);
    }
  }
  if (current) sections.push(current);
  return sections;
}

function parseRiskLevel(line) {
  const upper = line.toUpperCase();
  if (upper.includes('[HIGH]')) return { level: 'HIGH', color: 'bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-400 border-red-300 dark:border-red-800' };
  if (upper.includes('[MEDIUM]')) return { level: 'MEDIUM', color: 'bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-800' };
  if (upper.includes('[LOW]')) return { level: 'LOW', color: 'bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800' };
  return null;
}

function parseTimelinePhase(line) {
  const match = line.match(/Phase\s*(\d+)\s*\(([^)]+)\):\s*(.*)/i);
  if (match) return { phase: match[1], duration: match[2], desc: match[3] };
  const match2 = line.replace(/^[-*]\s*/, '').match(/^(Week\s*\d+[-–]\d+|Month\s*\d+[-–]\d+):\s*(.*)/i);
  if (match2) return { phase: null, duration: match2[1], desc: match2[2] };
  return null;
}

export function ActionPlanPanel({ rec, filters }) {
  const user = useUser();
  const isSuperAdmin = (user?.roles || []).includes('super_admin');
  const caps = user?.ai_capabilities || {};
  const hasActionPlanCap = isSuperAdmin || caps.ai_action_plans !== false;
  const hasActionPlan = (isSuperAdmin || (user?.subscribed_features || []).includes('action_plan')) && hasActionPlanCap;
  const hasUserAccess = user?.ai_access !== false && user?.action_plan_access !== false;
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [checklist, setChecklist] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchPlan = async () => {
    if (plan) { setOpen(true); return; }
    setLoading(true);
    setOpen(true);
    try {
      const res = await api.post('/ai/action-plan', {
        recommendation_title: rec.title,
        recommendation_description: rec.description,
        recommendation_category: rec.category,
        potential_impact: rec.potential_impact,
        filters: filters || {},
      });
      setPlan(res.data.action_plan);
    } catch {
      setPlan('Failed to generate action plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleCheck = (idx) => {
    setChecklist(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/ai/action-plan/save', {
        recommendation_title: rec.title,
        action_plan: plan,
        checklist_state: checklist,
      });
      toast.success('Action plan saved to Reports Inbox');
    } catch {
      toast.error('Failed to save action plan');
    } finally {
      setSaving(false);
    }
  };

  const handleExportPDF = () => {
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const sections = parseSections(plan);
    const html = `<!DOCTYPE html><html><head><title>Action Plan - ${esc(rec.title)}</title>
      <style>
        body { font-family: 'Segoe UI', sans-serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #1a1a2e; line-height: 1.6; }
        h1 { font-size: 20px; color: #001f5c; border-bottom: 2px solid #00aaff; padding-bottom: 8px; }
        h2 { font-size: 15px; color: #001f5c; margin-top: 24px; margin-bottom: 8px; }
        .roi-box { background: #fef3c7; border: 1px solid #f59e0b; border-radius: 8px; padding: 12px 16px; margin: 16px 0; }
        .checklist-item { display: flex; gap: 8px; margin: 4px 0; }
        .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; }
        .badge-high { background: #fee2e2; color: #b91c1c; }
        .badge-medium { background: #fef3c7; color: #92400e; }
        .badge-low { background: #d1fae5; color: #065f46; }
        .timeline { border-left: 3px solid #00aaff; padding-left: 16px; margin: 8px 0; }
        .timeline-item { margin-bottom: 12px; }
        .timeline-dur { font-weight: 600; color: #001f5c; }
        ul { padding-left: 20px; }
        li { margin: 4px 0; }
        .footer { margin-top: 40px; font-size: 11px; color: #888; border-top: 1px solid #ddd; padding-top: 8px; }
      </style></head><body>
      <h1>Action Plan: ${esc(rec.title)}</h1>
      <p style="color:#666;font-size:13px">${esc(rec.description)}</p>
      ${sections.map(s => {
        if (s.key === 'estimated roi') return `<div class="roi-box"><h2>${esc(s.heading)}</h2><p>${esc(s.lines.join(' '))}</p></div>`;
        if (s.key === 'quick-start checklist') return `<h2>${esc(s.heading)}</h2>${s.lines.map(l => `<div class="checklist-item">&#9744; ${esc(l.replace(/^[-*]\s*\[.\]\s*/, ''))}</div>`).join('')}`;
        if (s.key === 'key risks & mitigations') return `<h2>${esc(s.heading)}</h2><ul>${s.lines.map(l => {
          const r = parseRiskLevel(l); const badge = r ? `<span class="badge badge-${esc(r.level.toLowerCase())}">${esc(r.level)}</span> ` : '';
          return `<li>${badge}${esc(l.replace(/^[-*]\s*/, '').replace(/\[(HIGH|MEDIUM|LOW)\]\s*/i, ''))}</li>`;
        }).join('')}</ul>`;
        return `<h2>${esc(s.heading)}</h2><ul>${s.lines.map(l => `<li>${esc(l.replace(/^[-*]\s*/, ''))}</li>`).join('')}</ul>`;
      }).join('')}
      <div class="footer">Generated by SpendLens AI | ${new Date().toLocaleDateString()}</div>
      </body></html>`;
    // Avoid deprecated document.write: serve via a Blob URL rendered natively.
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    const printWindow = window.open(url, '_blank', 'noopener');
    if (!printWindow) return;
    printWindow.onload = () => {
      setTimeout(() => printWindow.print(), 500);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    };
  };

  const sections = plan ? parseSections(plan) : [];

  return (
    <div className="mt-2">
      {hasActionPlan && hasUserAccess ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs gap-1.5 text-primary hover:text-primary"
          onClick={() => open && plan ? setOpen(false) : fetchPlan()}
          disabled={loading}
          data-testid="action-plan-btn"
        >
          {loading ? (
            <><Loader2 className="w-3 h-3 animate-spin" />Generating Plan...</>
          ) : open ? (
            <><ChevronDown className="w-3 h-3" />Hide Action Plan</>
          ) : (
            <><ClipboardList className="w-3 h-3" />Action Plan</>
          )}
        </Button>
      ) : (
        <div className="relative inline-block group" data-testid="action-plan-locked">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1.5 text-muted-foreground cursor-not-allowed opacity-60"
            disabled
            data-testid="action-plan-btn-locked"
          >
            <Lock className="w-3 h-3" />
            Action Plan
          </Button>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2.5 py-1 rounded bg-popover border border-border shadow-md text-[10px] text-muted-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50" data-testid="action-plan-locked-tooltip">
            Subscribe to unlock AI Action Plans
          </div>
        </div>
      )}
      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mt-2 rounded-lg border border-border overflow-hidden"
          data-testid="action-plan-content"
        >
          {loading ? (
            <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Generating detailed action plan...</span>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="flex items-center justify-end gap-2 px-4 py-2 bg-muted/30 border-b border-border">
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1.5" onClick={handleExportPDF} data-testid="export-plan-pdf">
                  <Download className="w-3 h-3" />Export PDF
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1.5" onClick={handleSave} disabled={saving} data-testid="save-plan-btn">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  Save Plan
                </Button>
              </div>

              <div className="p-4 space-y-4">
                {sections.map((section, si) => {
                  const Icon = section.icon;

                  {/* ROI Callout Banner */}
                  if (section.key === 'estimated roi') {
                    return (
                      <div key={si} className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-4" data-testid="roi-banner">
                        <div className="flex items-center gap-2 mb-1">
                          <PoundSterling className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-bold text-primary uppercase tracking-wide">{section.heading}</span>
                        </div>
                        <p className="text-sm text-amber-900 dark:text-amber-200">{cleanMarkdown(section.lines.join(' '))}</p>
                      </div>
                    );
                  }

                  {/* Quick-Start Checklist */}
                  if (section.key === 'quick-start checklist') {
                    return (
                      <div key={si} className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/30 p-4" data-testid="quick-start-checklist">
                        <div className="flex items-center gap-2 mb-3">
                          <CheckSquare className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm font-bold text-primary uppercase tracking-wide">{section.heading}</span>
                        </div>
                        <div className="space-y-2">
                          {section.lines.map((line, li) => {
                            const text = cleanMarkdown(line.replace(/^[-*]\s*\[.\]\s*/, '').replace(/^[-*]\s*/, ''));
                            const checked = checklist[li] || false;
                            return (
                              <button key={li} onClick={() => toggleCheck(li)}
                                className={`flex items-start gap-2.5 w-full text-left p-2 rounded-md transition-colors text-xs ${checked ? 'bg-emerald-100/80 dark:bg-emerald-900/30' : 'hover:bg-emerald-100/50 dark:hover:bg-emerald-900/20'}`}
                                data-testid={`checklist-item-${li}`}>
                                {checked ? <CheckSquare className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> : <Square className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />}
                                <span className={checked ? 'line-through text-muted-foreground' : ''}>{text}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }

                  {/* Implementation Timeline */}
                  if (section.key === 'implementation timeline') {
                    return (
                      <div key={si} data-testid="timeline-section">
                        <div className="flex items-center gap-2 mb-3">
                          <Icon className={`w-4 h-4 ${section.color}`} />
                          <span className="text-sm font-bold text-primary uppercase tracking-wide">{section.heading}</span>
                        </div>
                        <div className="relative ml-2 border-l-2 border-primary/30 pl-4 space-y-3">
                          {section.lines.map((line, li) => {
                            const parsed = parseTimelinePhase(line.replace(/^[-*]\s*/, ''));
                            if (parsed) {
                              return (
                                <div key={li} className="relative">
                                  <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-primary border-2 border-background" />
                                  <p className="text-xs"><span className="font-semibold text-primary">{cleanMarkdown(parsed.duration)}</span></p>
                                  <p className="text-xs text-muted-foreground">{cleanMarkdown(parsed.desc)}</p>
                                </div>
                              );
                            }
                            return <p key={li} className="text-xs ml-1">{cleanMarkdown(line.replace(/^[-*]\s*/, ''))}</p>;
                          })}
                        </div>
                      </div>
                    );
                  }

                  {/* Risks with badges */}
                  if (section.key === 'key risks & mitigations') {
                    return (
                      <div key={si} data-testid="risks-section">
                        <div className="flex items-center gap-2 mb-3">
                          <Icon className={`w-4 h-4 ${section.color}`} />
                          <span className="text-sm font-bold text-primary uppercase tracking-wide">{section.heading}</span>
                        </div>
                        <div className="space-y-2">
                          {section.lines.map((line, li) => {
                            const risk = parseRiskLevel(line);
                            const cleaned = cleanMarkdown(line.replace(/^[-*]\s*/, '').replace(/\[(HIGH|MEDIUM|LOW)\]\s*/i, ''));
                            return (
                              <div key={li} className="flex items-start gap-2 text-xs p-2 rounded-md bg-muted/30">
                                {risk && <Badge className={`text-[10px] shrink-0 ${risk.color} border`}>{risk.level}</Badge>}
                                <span>{cleaned}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }

                  {/* Default section rendering */}
                  return (
                    <div key={si}>
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className={`w-4 h-4 ${section.color}`} />
                        <span className="text-sm font-bold text-primary uppercase tracking-wide">{section.heading}</span>
                      </div>
                      <div className="text-xs space-y-1 ml-6">
                        {section.lines.map((line, li) => {
                          const clean = cleanMarkdown(line);
                          if (line.startsWith('- ') || line.startsWith('* ')) {
                            return <p key={li} className="flex items-start gap-1.5"><span className="mt-1.5 w-1 h-1 rounded-full bg-foreground/40 shrink-0" />{cleanMarkdown(line.replace(/^[-*]\s*/, ''))}</p>;
                          }
                          return <p key={li}>{clean}</p>;
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </motion.div>
      )}
    </div>
  );
}

export function AutoRecommendations({ page, filters }) {
  const user = useUser();
  const isSuperAdmin = (user?.roles || []).includes('super_admin');
  const caps = user?.ai_capabilities || {};
  const hasRecsCap = isSuperAdmin || caps.ai_recommendations !== false;
  const hasOrgAccess = (isSuperAdmin || (user?.subscribed_features || []).includes('ai_recommendations')) && hasRecsCap;
  const hasUserAccess = user?.ai_access !== false;
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterContext, setFilterContext] = useState('');
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const lastFiltersRef = useRef(null);
  const fetchIdRef = useRef(0);

  const fetchRecommendations = useCallback(async (currentFilters) => {
    if (!hasOrgAccess || !hasUserAccess) return;
    const id = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/ai/recommendations', {
        page,
        filters: currentFilters || {},
      });
      if (id === fetchIdRef.current) {
        setRecommendations(res.data.recommendations || []);
        setFilterContext(res.data.filter_context || '');
        setLoading(false);
      }
    } catch (err) {
      if (id === fetchIdRef.current) {
        setError('Unable to generate recommendations');
        setLoading(false);
      }
    }
  }, [page]);

  useEffect(() => {
    const filterStr = JSON.stringify(filters || {});

    // First run or filter change
    if (lastFiltersRef.current === null) {
      // Initial load — fetch immediately
      lastFiltersRef.current = filterStr;
      fetchRecommendations(filters);
      return;
    }

    if (filterStr === lastFiltersRef.current) return;
    lastFiltersRef.current = filterStr;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchRecommendations(filters);
    }, 1500);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [filters, fetchRecommendations]);

  // If org or user lacks AI access, don't render
  if (!hasOrgAccess || !hasUserAccess) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen} data-testid="auto-recommendations">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 text-left" data-testid="recommendations-toggle">
                <ChevronRight className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`} />
                <Lightbulb className="w-5 h-5 text-amber-500" />
                <CardTitle className="text-base">{page === 'risk-dashboard' ? 'Risk Mitigation Recommendations' : 'AI Recommendations'}</CardTitle>
                {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                {!open && recommendations.length > 0 && (
                  <Badge variant="secondary" className="text-xs ml-1">{recommendations.length}</Badge>
                )}
              </button>
            </CollapsibleTrigger>
            <Button variant="ghost" size="sm" onClick={() => fetchRecommendations(filters)} disabled={loading} data-testid="refresh-recommendations">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          {open && filterContext && (filterContext !== 'No filters (all data)' || page === 'risk-dashboard') && (
            <p className="text-xs text-muted-foreground mt-1">Based on: {filterContext}</p>
          )}
        </CardHeader>
        <CollapsibleContent>
          <CardContent>
        {error && !loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
            <AlertTriangle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && recommendations.length === 0 && (
          <p className="text-sm text-muted-foreground py-3">Apply filters to get targeted recommendations</p>
        )}

        <AnimatePresence mode="wait">
          {!loading && recommendations.length > 0 && (
            <motion.div
              key={filterContext}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-3"
            >
              {recommendations.map((rec, i) => {
                const Icon = CATEGORY_ICONS[rec.category] || Lightbulb;
                const colorClass = CATEGORY_COLORS[rec.category] || CATEGORY_COLORS.strategic;
                return (
                  <motion.div
                    key={`${rec.title}-${i}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.08 }}
                    className={`p-3 rounded-lg border ${colorClass}`}
                    data-testid={`recommendation-${i}`}
                  >
                    <div className="flex items-start gap-2.5">
                      <div className="mt-0.5">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-sm text-primary uppercase tracking-wide">{cleanMarkdown(rec.title)}</span>
                          <div className={`w-2 h-2 rounded-full ${PRIORITY_STYLES[rec.priority] || PRIORITY_STYLES.medium}`} title={`${rec.priority} priority`} />
                        </div>
                        <p className="text-xs leading-relaxed opacity-80">{cleanMarkdown(rec.description)}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          {rec.potential_impact && (
                            <Badge variant="outline" className="text-[10px] font-medium">
                              Impact: {cleanMarkdown(rec.potential_impact)}
                            </Badge>
                          )}
                        </div>
                        <ActionPlanPanel rec={rec} filters={filters} />
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>

        {loading && (
          <div className="space-y-3 py-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse flex gap-2.5 p-3 rounded-lg bg-muted/30">
                <div className="w-4 h-4 bg-muted rounded mt-0.5" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 bg-muted rounded w-2/3" />
                  <div className="h-2.5 bg-muted rounded w-full" />
                  <div className="h-2.5 bg-muted rounded w-4/5" />
                </div>
              </div>
            ))}
          </div>
        )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
